export interface ImagePreviewItem {
  src: string
  label?: string
  ratio?: number
}

export interface ImagePreviewSpec {
  src?: string
  title?: string
  items?: ImagePreviewItem[]
}

const IMAGE_EXTENSION_PATTERN = /\.(?:png|jpe?g|gif|webp|svg|bmp|ico|avif)(?:[?#].*)?$/i
const WINDOWS_FILE_PATH_PATTERN = /^[A-Za-z]:[\\/].+\.(?:png|jpe?g|gif|webp|svg|bmp|ico|avif)$/i
const POSIX_FILE_PATH_PATTERN = /^(?:\/|\.\/|\.\.\/|~\/).+\.(?:png|jpe?g|gif|webp|svg|bmp|ico|avif)$/i

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value)
}

function pickString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined
}

function pickNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined
}

export function isRemoteImageUrl(value: string): boolean {
  if (!/^https?:\/\//i.test(value)) return false
  try {
    const url = new URL(value)
    return IMAGE_EXTENSION_PATTERN.test(url.pathname)
  } catch {
    return false
  }
}

export function isDirectImageSource(value: string): boolean {
  const trimmed = value.trim()
  return trimmed.startsWith('data:image/') || isRemoteImageUrl(trimmed)
}

export function isRenderableImageSource(value: string): boolean {
  const trimmed = value.trim()
  return (
    isDirectImageSource(trimmed) ||
    WINDOWS_FILE_PATH_PATTERN.test(trimmed) ||
    POSIX_FILE_PATH_PATTERN.test(trimmed)
  )
}

function toImagePreviewItem(value: unknown): ImagePreviewItem | null {
  if (typeof value === 'string') {
    return isRenderableImageSource(value) ? { src: value.trim() } : null
  }

  if (!isRecord(value)) return null

  const src = [
    pickString(value.src),
    pickString(value.url),
    pickString(value.image_url),
    pickString(value.imageUrl),
  ].find((candidate): candidate is string => !!candidate && isRenderableImageSource(candidate))

  if (!src) return null

  return {
    src,
    label: pickString(value.label) ?? pickString(value.title),
    ratio: pickNumber(value.ratio),
  }
}

export function extractImagePreviewSpec(value: unknown): ImagePreviewSpec | null {
  if (typeof value === 'string') {
    return isRenderableImageSource(value) ? { src: value.trim() } : null
  }

  if (Array.isArray(value)) {
    const items = value
      .map(toImagePreviewItem)
      .filter((item): item is ImagePreviewItem => item !== null)

    if (items.length === 0) return null
    if (items.length === 1) {
      const first = items[0]
      if (!first) return null
      return {
        src: first.src,
        title: first.label,
      }
    }
    return { items }
  }

  if (!isRecord(value)) return null

  if (Array.isArray(value.items)) {
    const items = value.items
      .map(toImagePreviewItem)
      .filter((item): item is ImagePreviewItem => item !== null)

    if (items.length > 0) {
      return {
        title: pickString(value.title),
        items,
      }
    }
  }

  const directSource = [
    pickString(value.src),
    pickString(value.url),
    pickString(value.image_url),
    pickString(value.imageUrl),
  ].find((candidate): candidate is string => !!candidate && isRenderableImageSource(candidate))

  if (directSource) {
    return {
      src: directSource,
      title: pickString(value.title) ?? pickString(value.label),
    }
  }

  const arraySource = [value.urls, value.images].find(Array.isArray)
  if (Array.isArray(arraySource)) {
    const items = arraySource
      .map(toImagePreviewItem)
      .filter((item): item is ImagePreviewItem => item !== null)

    if (items.length > 0) {
      const first = items[0]
      return items.length === 1
        ? { src: first?.src, title: pickString(value.title) ?? first?.label }
        : { title: pickString(value.title), items }
    }
  }

  return null
}

function parseJsonCandidate(candidate: string): ImagePreviewSpec | null {
  try {
    return extractImagePreviewSpec(JSON.parse(candidate))
  } catch {
    return null
  }
}

export function extractImagePreviewSpecFromText(text: string): ImagePreviewSpec | null {
  const trimmed = text.trim()
  if (!trimmed) return null

  const previewFenceMatch = trimmed.match(/```image-preview\s*\n([\s\S]*?)\n```/)
  if (previewFenceMatch?.[1]) {
    const spec = parseJsonCandidate(previewFenceMatch[1])
    if (spec) return spec
  }

  if (isRenderableImageSource(trimmed)) {
    return { src: trimmed }
  }

  const directJson = parseJsonCandidate(trimmed)
  if (directJson) return directJson

  const fencedCodePattern = /```(?:[^\n`]*)\n([\s\S]*?)\n```/g
  for (const match of trimmed.matchAll(fencedCodePattern)) {
    const candidate = match[1]?.trim()
    if (!candidate) continue
    if (isRenderableImageSource(candidate)) {
      return { src: candidate }
    }
    const spec = parseJsonCandidate(candidate)
    if (spec) return spec
  }

  return null
}

export function stringifyImagePreviewSpec(spec: ImagePreviewSpec): string {
  return JSON.stringify(spec, null, 2)
}
